import React, { useState, useEffect } from 'react';
import { Trophy, Target, AlertTriangle, TrendingUp, Award, Users } from 'lucide-react';
import toast from 'react-hot-toast';
import axios from 'axios';

const TournamentStatistics = ({ user, t, title = null, className = '' }) => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [tournaments, setTournaments] = useState([]);

  useEffect(() => {
    const loadTournamentStatistics = async () => {
      setLoading(true);
      setError(null);
      
      try {
        let response;
        let userTournaments = [];
        let allTournaments = [];
        
        // Function to fetch all tournaments with pagination
        const fetchAllTournaments = async (endpoint, baseParams = {}) => {
          let page = 1;
          let hasMore = true;
          let allData = [];
          
          while (hasMore) {
            try {
              const pageResponse = await axios.get(endpoint, {
                params: {
                  ...baseParams,
                  page: page,
                  limit: 100,
                  skip: (page - 1) * 100
                }
              });
              
              const data = pageResponse.data;
              if (Array.isArray(data)) {
                allData = [...allData, ...data];
                hasMore = data.length === 100;
              } else if (data.tournaments && Array.isArray(data.tournaments)) {
                allData = [...allData, ...data.tournaments];
                hasMore = data.tournaments.length === 100;
              } else {
                hasMore = false;
              }
              
              page++;
              if (page > 50) break;
              
            } catch (error) {
              console.log(`Pagination failed at page ${page}:`, error.message);
              hasMore = false;
            }
          }
          
          return allData;
        };
        
        try {
          // Try the history endpoint first
          try {
            response = await axios.get('/api/tournaments/history', {
              params: {
                limit: 'all',
                userId: user._id,
                all: true
              }
            });
            allTournaments = Array.isArray(response.data) ? response.data : [];
          } catch (singleCallError) {
            allTournaments = await fetchAllTournaments('/api/tournaments/history', {
              userId: user._id,
              all: true
            });
          }
          
          console.log('Tournament Statistics API response:', allTournaments);
        } catch (historyError) {
          console.log('History endpoint failed, trying main tournaments endpoint:', historyError.message);
          
          try {
            response = await axios.get('/api/tournaments', {
              params: {
                limit: 0,
                all: true,
                status: 'completed'
              }
            });
            allTournaments = Array.isArray(response.data) ? response.data : [];
          } catch (limitError) {
            try {
              response = await axios.get('/api/tournaments', {
                params: {
                  limit: 1000,
                  all: true
                }
              });
              allTournaments = Array.isArray(response.data) ? response.data : [];
            } catch (highLimitError) {
              try {
                allTournaments = await fetchAllTournaments('/api/tournaments', { all: true });
              } catch (paginationError) {
                response = await axios.get('/api/tournaments');
                allTournaments = Array.isArray(response.data) ? response.data : [];
              }
            }
          }
          console.log('Main tournaments API response:', allTournaments);
        }
        
        if (allTournaments && Array.isArray(allTournaments) && allTournaments.length > 0) {
          userTournaments = allTournaments.filter(tournament => {
            const isCompleted = tournament.status === 'completed' || 
                              tournament.completedAt || 
                              (tournament.winners && tournament.winners.length > 0);
            
            if (!isCompleted) return false;
            
            let userParticipated = false;
            
            if (tournament.participants && Array.isArray(tournament.participants)) {
              userParticipated = tournament.participants.some(participant => {
                if (typeof participant === 'string') {
                  return participant === user._id;
                } else if (participant && typeof participant === 'object') {
                  return participant._id === user._id || 
                         participant.id === user._id ||
                         participant === user._id;
                }
                return false;
              });
            }
            
            if (!userParticipated && tournament.winners && Array.isArray(tournament.winners)) {
              userParticipated = tournament.winners.some(winner => {
                if (typeof winner === 'string') {
                  return winner === user._id;
                } else if (winner && typeof winner === 'object') {
                  return winner._id === user._id || 
                         winner.id === user._id;
                }
                return false;
              });
            }
            
            if (!userParticipated && tournament.creator) {
              if (typeof tournament.creator === 'string') {
                userParticipated = tournament.creator === user._id;
              } else if (tournament.creator && typeof tournament.creator === 'object') {
                userParticipated = tournament.creator._id === user._id || 
                                  tournament.creator.id === user._id;
              }
            }
            
            return userParticipated;
          });
          
          userTournaments.sort((a, b) => {
            const dateA = new Date(a.completedAt || a.updatedAt || a.createdAt);
            const dateB = new Date(b.completedAt || b.updatedAt || b.createdAt);
            return dateB - dateA;
          });
          
          console.log('Filtered user tournaments:', userTournaments);
        }
        
        setTournaments(userTournaments);
      } catch (error) {
        console.error('Error loading tournament statistics:', error);
        setError(error.message);
        toast.error(t('failedToLoadTournamentStats', 'Failed to load tournament statistics'));
      } finally {
        setLoading(false);
      }
    };

    loadTournamentStatistics();
  }, [user._id, t]);

  // Helper function to check if user won a tournament
  const didUserWin = (tournament) => {
    if (!tournament.winners || !Array.isArray(tournament.winners)) return false;
    
    return tournament.winners.some(winner => {
      if (typeof winner === 'string') {
        return winner === user._id;
      } else if (winner && typeof winner === 'object') {
        return winner._id === user._id || winner.id === user._id;
      }
      return false;
    });
  };

  // Performance level calculation
  const getPerformanceLevel = (winRate) => {
    const rate = parseFloat(winRate);
    if (rate >= 80) return { level: 'elite', text: t('elite', 'Elite'), icon: Award };
    if (rate >= 70) return { level: 'pro', text: t('pro', 'Pro'), icon: Trophy };
    if (rate >= 60) return { level: 'advanced', text: t('advanced', 'Advanced'), icon: TrendingUp };
    if (rate >= 40) return { level: 'average', text: t('average', 'Average'), icon: Target };
    return { level: 'beginner', text: t('beginner', 'Beginner'), icon: Users };
  };

  // Calculate statistics
  const totalTournaments = tournaments.length;
  const wins = tournaments.filter(tournament => didUserWin(tournament)).length;
  const losses = totalTournaments - wins;
  const winRate = totalTournaments > 0 ? ((wins / totalTournaments) * 100).toFixed(1) : '0';
  const wlRatio = losses === 0 ? (wins > 0 ? wins.toFixed(1) : '0') : (wins / losses).toFixed(1);

  const performance = getPerformanceLevel(winRate);
  const PerformanceIcon = performance.icon;

  // Loading state
  if (loading) {
    return (
      <div className={`stats-section ${className}`}>
        <div className="stats-loading">
          <div className="stats-spinner"></div>
          <span className="stats-loading-text">{t('loadingStatistics', 'Loading statistics...')}</span>
        </div>
      </div>
    );
  }

  // Error state
  if (error) {
    return (
      <div className={`stats-section ${className}`}>
        <div className="error-state">
          <AlertTriangle className="w-12 h-12 text-red-400 mx-auto mb-3" />
          <p className="text-red-400">{t('errorLoadingStatistics', 'Error loading statistics')}</p>
          <p className="text-sm text-gray-500">{error}</p>
        </div>
      </div>
    );
  }

  return (
    <div className={`stats-section ${className}`}>
      <div className="stats-header">
        <h3 className="stats-title">
          <Trophy className="stats-title-icon" />
          {title || t('tournamentStatistics', 'Tournament Statistics')}
        </h3>
        
        <div className="performance-badge">
          <PerformanceIcon className="performance-icon" />
          <span className={`performance-level performance-${performance.level}`}>
            {performance.text}
          </span>
        </div>
      </div>
      
      <div className="stats-container">
        <div className="stats-grid">
          {/* Total Matches */}
          <div className="stat-card stat-total">
            <div className="stat-card-inner">
              <div className="stat-icon-wrapper">
                <Target className="stat-icon" />
              </div>
              <div className="stat-content">
                <div className="stat-value">{totalTournaments}</div>
                <div className="stat-label">{t('totalMatches', 'Total Matches')}</div>
              </div>
              <div className="stat-decoration"></div>
            </div>
          </div>

          {/* Wins */}
          <div className="stat-card stat-wins">
            <div className="stat-card-inner">
              <div className="stat-icon-wrapper">
                <Trophy className="stat-icon" />
              </div>
              <div className="stat-content">
                <div className="stat-value">{wins}</div>
                <div className="stat-label">{t('wins', 'Wins')}</div>
              </div>
              <div className="stat-decoration"></div>
            </div>
          </div>

          {/* Losses */}
          <div className="stat-card stat-losses">
            <div className="stat-card-inner">
              <div className="stat-icon-wrapper">
                <AlertTriangle className="stat-icon" />
              </div>
              <div className="stat-content">
                <div className="stat-value">{losses}</div>
                <div className="stat-label">{t('losses', 'Losses')}</div>
              </div>
              <div className="stat-decoration"></div>
            </div>
          </div>

          {/* Win Rate */}
          <div className="stat-card stat-winrate">
            <div className="stat-card-inner">
              <div className="stat-icon-wrapper">
                <TrendingUp className="stat-icon" />
              </div>
              <div className="stat-content">
                <div className="stat-value">{winRate}%</div>
                <div className="stat-label">{t('winRate', 'Win Rate')}</div>
              </div>
              <div className="stat-progress">
                <div 
                  className="stat-progress-fill"
                  style={{ width: `${winRate}%` }}
                ></div>
              </div>
            </div>
          </div>

          {/* W/L Ratio */}
          <div className="stat-card stat-ratio">
            <div className="stat-card-inner">
              <div className="stat-icon-wrapper">
                <Award className="stat-icon" />
              </div>
              <div className="stat-content">
                <div className="stat-value">{wlRatio}</div>
                <div className="stat-label">{t('wlRatio', 'W/L Ratio')}</div>
              </div>
              <div className="stat-decoration"></div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default TournamentStatistics;